import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { endOfMonth } from 'date-fns'
import {
  BenefitsMyBenefitsTransactionsDocument,
  BenefitsMyBenefitsTransactionsFragment,
  BenefitsValueObjectsBenefitTransactionCategoryEnum,
  BenefitsValueObjectsBenefitTransactionCompensationStatusEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

// TODO: work with f0 to get an exported type, derived from StatusVariant
type StatusCellType = 'neutral' | 'info' | 'positive' | 'warning' | 'critical'

const BENEFIT_COMPENSATION_STATUS_MAPPING: Record<
  BenefitsValueObjectsBenefitTransactionCompensationStatusEnum,
  StatusCellType
> = {
  pending: 'info',
  ready: 'positive',
  completed: 'positive',
  reversed: 'warning',
  discarded: 'critical',
} as const

const getBenefitCompensationStatus = (
  status: BenefitsValueObjectsBenefitTransactionCompensationStatusEnum
): StatusCellType => {
  return BENEFIT_COMPENSATION_STATUS_MAPPING[status] || 'neutral'
}

interface MyBenefitsHomePageProps {
  benefitCategoriesList: BenefitsValueObjectsBenefitTransactionCategoryEnum[]
}

const MyBenefitsHome = ({ benefitCategoriesList }: MyBenefitsHomePageProps) => {
  const compensationStatusOptions: Array<{
    label: string
    value: BenefitsValueObjectsBenefitTransactionCompensationStatusEnum
  }> = [
    {
      label: i18n.t('benefits.my_benefits.spending.transactions.statuses.pending'),
      value: 'pending',
    },
    { label: i18n.t('benefits.my_benefits.spending.transactions.statuses.ready'), value: 'ready' },
    {
      label: i18n.t('benefits.my_benefits.spending.transactions.statuses.completed'),
      value: 'completed',
    },
    {
      label: i18n.t('benefits.my_benefits.spending.transactions.statuses.reversed'),
      value: 'reversed',
    },
    {
      label: i18n.t('benefits.my_benefits.spending.transactions.statuses.discarded'),
      value: 'discarded',
    },
  ]

  const labelMap: Record<BenefitsValueObjectsBenefitTransactionCategoryEnum, string> = {
    meals: i18n.t('benefits.homepage.static_benefits.es.meals.title'),
    transportation: i18n.t('benefits.homepage.static_benefits.es.transport.title'),
    childcare: i18n.t('benefits.homepage.static_benefits.es.nursery.title'),
    training: i18n.t('benefits.homepage.static_benefits.es.trainings.title'),
    healthcare: i18n.t('benefits.homepage.static_benefits.es.health_insurance.title'),
    unsupported: i18n.t('benefits.homepage.static_benefits.es.unsupported.title'),
    wellness: i18n.t('benefits.homepage.static_benefits.es.wellness.title'),
  }

  const categoryOptions: Array<{
    label: string
    value: BenefitsValueObjectsBenefitTransactionCategoryEnum
  }> = benefitCategoriesList.map((category) => {
    return {
      label: labelMap[category] || category,
      value: category,
    }
  })

  const { employee, access } = useCurrentSession()
  const accessLocale = access?.locale

  const source = useGraphqlDataCollectionSource({
    document: BenefitsMyBenefitsTransactionsDocument,
    mapVariables: ({ sortings, filters, search, navigationFilters }) => {
      return {
        dateFrom: navigationFilters?.date?.value?.from,
        dateTo: navigationFilters?.date?.value?.to,
        employeeIds: employee?.id ? [employee.id] : [-1],
        sortOrder: sortings?.[0],
        categories: filters?.category,
        compensationStatuses: filters?.status,
        search,
      }
    },
    search: {
      enabled: true,
    },
    totalItemSummary: (totalItems) => {
      if (totalItems === 0) {
        return i18n.t('benefits.my_benefits.spending.transactions.zero')
      }
      return i18n.tp('benefits.my_benefits.spending.transactions', {
        count: totalItems,
      })
    },
    navigationFilters: {
      date: {
        type: 'date-navigator',
        defaultValue: new Date(),
        hideGoToCurrent: true,
        defaultGranularity: 'month',
        granularity: ['week', 'month', 'day'],
        max: endOfMonth(new Date()),
      },
    },
    sortings: {
      concept: {
        label: i18n.t('benefits.my_benefits.spending.transactions.columns.concept'),
      },
      createdAt: {
        label: i18n.t('benefits.my_benefits.spending.transactions.columns.date'),
      },
      amountCents: {
        label: i18n.t('benefits.my_benefits.spending.transactions.columns.amount'),
      },
    },
    defaultSortings: {
      field: 'createdAt',
      order: 'desc',
    },
    filters: {
      status: {
        type: 'in',
        label: i18n.t('benefits.my_benefits.spending.transactions.columns.status'),
        options: {
          options: compensationStatusOptions,
        },
      },
      category: {
        type: 'in',
        label: i18n.t('benefits.my_benefits.spending.transactions.columns.category'),
        options: {
          options: categoryOptions,
        },
      },
    },

    mapData: (data) => data.benefits.benefitTransactionsConnection.nodes,
    mapPagination: (data) => ({
      total: data.benefits.benefitTransactionsConnection.totalCount ?? 0,
    }),
  })
  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('benefits.my_benefits.spending.transactions.columns.concept'),
                sorting: 'concept',
                render: (tx: BenefitsMyBenefitsTransactionsFragment) => tx.concept || '-',
              },
              {
                label: i18n.t('benefits.my_benefits.spending.transactions.columns.date'),
                sorting: 'createdAt',
                render: (tx: BenefitsMyBenefitsTransactionsFragment) => ({
                  type: 'date',
                  value: convertDate(tx.createdAt, 'backend_datetime', 'ui'),
                }),
              },
              {
                label: i18n.t('benefits.my_benefits.spending.transactions.columns.amount'),
                sorting: 'amountCents',
                render: (tx: BenefitsMyBenefitsTransactionsFragment) =>
                  MoneyWithCurrency({
                    amount: -tx.amountCents,
                    currency: 'EUR',
                    locale: accessLocale,
                  }),
              },
              {
                label: i18n.t('benefits.my_benefits.spending.transactions.columns.status'),

                render: (tx: BenefitsMyBenefitsTransactionsFragment) => ({
                  type: 'status',
                  value: {
                    status: getBenefitCompensationStatus(tx.compensationStatus),
                    label: compensationStatusOptions.find(
                      (option) => option.value === tx.compensationStatus
                    )?.label,
                  },
                }),
              },
              {
                label: i18n.t('benefits.my_benefits.spending.transactions.columns.category'),
                render: (tx: BenefitsMyBenefitsTransactionsFragment) => labelMap[tx.category],
              },
            ],
          },
        },
      ]}
    />
  )
}

export default MyBenefitsHome
